import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CircleX } from 'lucide-react'
import { regenerateBackupCodes } from '../../api/mfa'
import { keys } from '../../api/keys'
import { Button } from '../../platform/primitives/Button'
import { Icon } from '../../platform/icons/Icon'
import { Overlay } from '../../platform/overlay/Overlay'
import { useToast } from '../../platform/feedback/ToastProvider'
import { ReauthDialog } from './ReauthDialog'
import { StepUpSheet } from './StepUpSheet'
import { CANCELLED_MESSAGE } from './mfaCopy'
import { useGatedMfaCall } from './useGatedMfaCall'
import './mfaSheet.css'

type Props = {
  open: boolean
  onClose: () => void
  /** How many are left now — the warning is only meaningful with a set to lose. */
  remaining: number
}

/**
 * Recovery codes, as a full-screen sheet with one step per screen — M13-R28,
 * R3, R29.
 *
 * 1. **Warn**, before anything happens: new codes invalidate the old set.
 * 2. **Step up.** `reauth_required` (sign-in too old) opens the Firebase
 *    `ReauthDialog`; `step_up_required` opens the backend's step-up
 *    (`StepUpSheet`). Either way the request is then repeated, once; a step-up
 *    token is spent by that one retry and never kept. `useGatedMfaCall` owns
 *    that sequence.
 * 3. **Show once**, with copy-all.
 *
 * **The codes live in this component's state and nowhere else** — not the query
 * cache, not storage, not a log, not analytics (rule 7). The inner flow is
 * mounted only while the sheet is open, so closing unmounts it and the codes go
 * with it. Closing invalidates the count so the row shows the new total.
 */
export function RecoveryCodesSheet({ open, onClose, remaining }: Props) {
  const queryClient = useQueryClient()
  const close = useCallback(() => {
    onClose()
    void queryClient.invalidateQueries({ queryKey: keys.mfa.status() })
  }, [onClose, queryClient])
  return (
    <Overlay open={open} onClose={close} label="Recovery codes" placement="sheet">
      {open ? <Flow remaining={remaining} onClose={close} /> : null}
    </Overlay>
  )
}

function Flow({ remaining, onClose }: { remaining: number; onClose: () => void }) {
  const [codes, setCodes] = useState<string[] | null>(null)
  const { show } = useToast()
  const { run, busy, error, cancelled, reauthProps, stepUpProps } = useGatedMfaCall({
    call: regenerateBackupCodes,
    onDone: setCodes,
    fallback: 'We couldn’t make new codes. Your old codes still work — try again.',
    unavailable: 'We couldn’t reach the server. Your old codes still work — try again.',
  })

  async function copyAll() {
    if (codes === null) return
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      show({ kind: 'success', message: 'Codes copied.' })
    } catch {
      show({ kind: 'error', message: 'Couldn’t copy. Select the codes and copy them yourself.' })
    }
  }

  if (codes !== null) {
    return (
      <div className="mfa-sheet">
        <h2 className="dialog__title">Your new recovery codes</h2>
        <p className="dialog__body">
          Save these now. They&apos;re shown once and can&apos;t be shown again. Each works one
          time.
        </p>
        <ul className="mfa-sheet__codes" aria-label="Recovery codes">
          {codes.map((code) => (
            <li key={code}>
              <code>{code}</code>
            </li>
          ))}
        </ul>
        <div className="dialog__actions">
          <Button variant="primary" size="page" onClick={onClose}>
            I’ve saved them
          </Button>
          <Button variant="outline" size="page" onClick={() => void copyAll()}>
            Copy all
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="mfa-sheet">
      <h2 className="dialog__title">Make new recovery codes?</h2>
      <p className="dialog__body">
        {remaining > 0
          ? `This stops your ${remaining} unused ${remaining === 1 ? 'code' : 'codes'} working straight away, and gives you a new set of 10.`
          : 'This gives you a new set of 10 codes.'}{' '}
        Recovery codes confirm sensitive changes on your account. They don&apos;t sign you in.
      </p>
      {error === null && cancelled && <p className="dialog__body">{CANCELLED_MESSAGE}</p>}
      {error !== null && (
        <p className="dialog__error" role="alert">
          <span className="dialog__error-mark">
            <Icon glyph={CircleX} />
          </span>
          {error}
        </p>
      )}
      <div className="dialog__actions">
        <Button variant="primary" size="page" disabled={busy} onClick={() => void run()}>
          {busy ? 'Making codes…' : 'Replace my codes'}
        </Button>
        <Button variant="outline" size="page" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>
      <ReauthDialog
        {...reauthProps}
        mode="regenerate_recovery_codes"
        title="Confirm it's you"
        body="For your security, confirm your identity again before making new recovery codes."
        confirmLabel="Continue"
        pendingLabel="Checking…"
      />
      <StepUpSheet {...stepUpProps} />
    </div>
  )
}
